import apiTamboUrl, { apiService } from "@/services/api";

export default {
  namespaced: true,
  state: {
    lotesMapeados: [],
    caminosMapeados: [],
    tamboGeo: null,
    loteSeleccionado: null,
    estadoAgronomico: null,
    loading: false,
    error: null,
  },
  getters: {
    lotesMapeados: (state) => state.lotesMapeados,
    caminosMapeados: (state) => state.caminosMapeados,
    tamboGeo: (state) => state.tamboGeo,
    loteSeleccionado: (state) => state.loteSeleccionado,
    estadoAgronomico: (state) => state.estadoAgronomico,
    loading: (state) => state.loading,
    error: (state) => state.error,
  },
  mutations: {
    setLoading(state, val) {
      state.loading = val;
    },
    setError(state, val) {
      state.error = val;
    },
    setMapaCompleto(state, { tambo, lotes }) {
      state.tamboGeo = tambo;
      state.lotesMapeados = lotes;
    },
    setCaminos(state, caminos) {
      state.caminosMapeados = caminos;
    },
    setLoteSeleccionado(state, lote) {
      state.loteSeleccionado = lote;
    },
    setEstadoAgronomico(state, estado) {
      state.estadoAgronomico = estado;
    },
    updateLoteMapeado(state, updatedLote) {
      const idx = state.lotesMapeados.findIndex(
        (l) => l.lote_id === updatedLote.lote_id
      );
      if (idx !== -1) {
        state.lotesMapeados[idx] = {
          ...state.lotesMapeados[idx],
          ...updatedLote,
        };
      }
    },
    addLoteMapeado(state, newLote) {
      state.lotesMapeados.push(newLote);
    },
  },
  actions: {
    async executeCall(
      { commit },
      { actionFn, errorMsg = "Error en la operación" }
    ) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const response = await actionFn();
        return response.data || response;
      } catch (err) {
        console.error(errorMsg, err);
        const detail = err.response?.data?.detail || err.message || errorMsg;
        commit("setError", detail);
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },

    async cargarMapaCompleto({ commit, rootState }) {
      const tamboId =
        rootState?.userTamboStore?.currentTamboId ||
        localStorage.getItem("current_tambo_id");
      try {
        const response = await apiTamboUrl.get("/lotes_vacas/mapa-completo");
        const data = response.data;

        // Cache el mapa agronómico cargado para soporte offline
        if (tamboId) {
          localStorage.setItem(
            "tambo_offline_mapa_completo_" + tamboId,
            JSON.stringify(data)
          );
          console.log(
            `💾 Caché de mapa agronómico actualizado para Tambo ${tamboId}`
          );
        }

        commit("setMapaCompleto", { tambo: data.tambo, lotes: data.lotes });
        return data;
      } catch (err) {
        // En caso de error de red (offline) o 422/404 (endpoint ausente en beta lite)
        const cached = localStorage.getItem(
          "tambo_offline_mapa_completo_" + tamboId
        );
        if (cached) {
          console.warn(
            "⚠️ Sin conexión o endpoint ausente. Cargando mapa agronómico en modo offline desde el almacenamiento local."
          );
          const data = JSON.parse(cached);
          commit("setMapaCompleto", { tambo: data.tambo, lotes: data.lotes });
          commit("setError", null); // Limpiamos el error visual para que no moleste
          return data;
        }

        // Fallback vacío para no romper el mapa
        const fallbackData = { tambo: null, lotes: [] };
        commit("setMapaCompleto", fallbackData);
        return fallbackData;
      }
    },

    async cargarCaminos({ commit, rootState }) {
      const tamboId =
        rootState?.userTamboStore?.currentTamboId ||
        localStorage.getItem("current_tambo_id");
      if (!tamboId) return [];
      try {
        const response = await apiTamboUrl.get(`/caminos/${tamboId}`);
        commit("setCaminos", response.data);
        return response.data;
      } catch (error) {
        if (
          error.response &&
          (error.response.status === 404 || error.response.status === 422)
        ) {
          commit("setCaminos", []);
          return [];
        }
        console.error("Error al cargar los caminos:", error);
        throw error;
      }
    },

    async recalcularCaminos({ dispatch, commit, rootState }) {
      const tamboId =
        rootState?.userTamboStore?.currentTamboId ||
        localStorage.getItem("current_tambo_id");
      if (!tamboId) return [];
      return await dispatch("executeCall", {
        actionFn: () => apiService.postCustom(`/caminos/${tamboId}/recalcular`),
        errorMsg: "Error al recalcular los caminos",
      }).then((data) => {
        commit("setCaminos", data);
        return data;
      });
    },

    async guardarGeometriaCamino({ dispatch, commit }, { caminoId, geojson }) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(`/caminos/${caminoId}/geometria`, {
            geojson_geometria: geojson,
          }),
        errorMsg: "Error al guardar la geometría del camino",
      }).then(async (data) => {
        await dispatch("cargarCaminos");
        return data;
      });
    },

    async crearCamino(
      { dispatch, commit, rootState },
      { nombre, geojson_geometria, tipo_suelo }
    ) {
      const tamboId =
        rootState?.userTamboStore?.currentTamboId ||
        localStorage.getItem("current_tambo_id");
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.postCustom(`/caminos/${tamboId}`, {
            nombre,
            geojson_geometria,
            tipo_suelo: tipo_suelo || "tierra",
          }),
        errorMsg: "Error al crear el camino",
      }).then(async (data) => {
        await dispatch("cargarCaminos");
        return data;
      });
    },

    async cargarEstadoAgronomico({ dispatch, commit }, loteId) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.getCustom(`/lotes_vacas/${loteId}/estado-agronomico`),
        errorMsg: "Error al cargar el estado agronómico del lote",
      }).then((data) => {
        commit("setEstadoAgronomico", data);
        return data;
      });
    },

    async actualizarNdvi({ dispatch, commit }, loteId) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.postCustom(`/lotes_vacas/${loteId}/actualizar-ndvi`, {}),
        errorMsg: "Error al actualizar el NDVI",
      }).then((data) => {
        commit("updateLoteMapeado", {
          lote_id: loteId,
          ultimo_ndvi: data.ndvi_promedio,
          ndvi_fecha: data.fecha,
        });
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    async geocodeLocalidad({ dispatch }, q) {
      return await dispatch("executeCall", {
        actionFn: () => apiService.getCustom(`/tambos/geocode?q=${q}`),
        errorMsg: "Error al buscar la localidad",
      });
    },

    async guardarUbicacionTambo(
      { dispatch, commit },
      { tamboId, latitud, longitud, localidad }
    ) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(`/tambos/${tamboId}/ubicacion`, {
            latitud,
            longitud,
            localidad,
          }),
        errorMsg: "Error al guardar la ubicación del tambo",
      }).then(async (data) => {
        // Actualiza el tamboGeo en el state
        await dispatch("cargarMapaCompleto");
        return data;
      });
    },

    async asociarRecurso({ dispatch, commit }, { loteId, tipo, recursoId }) {
      // tipo: 'pastura' or 'siembra'
      // recursoId: pastura.id or siembra.id
      const endpoint =
        tipo === "pastura" ? `pasturas/${recursoId}` : `siembras/${recursoId}`;
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(`/${endpoint}`, { lote_id: loteId }),
        errorMsg: `Error al asociar ${tipo} al lote`,
      }).then((data) => {
        // Recargar datos tras asociar
        dispatch("cargarMapaCompleto");
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    async desasociarRecurso({ dispatch, commit }, { loteId, tipo, recursoId }) {
      const endpoint =
        tipo === "pastura" ? `pasturas/${recursoId}` : `siembras/${recursoId}`;
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(`/${endpoint}`, { lote_id: null }),
        errorMsg: `Error al desasociar ${tipo} del lote`,
      }).then((data) => {
        dispatch("cargarMapaCompleto");
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    async guardarPoligonoLote(
      { dispatch, commit },
      { loteId, geojson, hectareas, skipReload }
    ) {
      // Usar endpoint para guardar el polígono y las hectáreas en el lote
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(
            `/lotes_vacas/editar_estancias_nombres/${loteId}`,
            {
              geojson_geometria: geojson,
              hectareas: hectareas,
            }
          ),
        errorMsg: "Error al guardar la geometría del lote",
      }).then((data) => {
        if (!skipReload) {
          dispatch("cargarMapaCompleto");
          dispatch("cargarEstadoAgronomico", loteId);
        }
        return data;
      });
    },

    async eliminarLote({ dispatch }, loteId) {
      return await dispatch("executeCall", {
        actionFn: () => apiService.deleteCustom(`lotes_vacas/${loteId}`),
        errorMsg: "Error al eliminar el lote",
      }).then((data) => {
        dispatch("cargarMapaCompleto");
        return data;
      });
    },

    async eliminarCamino({ dispatch }, caminoId) {
      return await dispatch("executeCall", {
        actionFn: () => apiService.deleteCustom(`caminos/${caminoId}`),
        errorMsg: "Error al eliminar el camino",
      }).then((data) => {
        dispatch("cargarCaminos");
        return data;
      });
    },

    async crearLote(
      { dispatch },
      {
        nombre_lote,
        codigo,
        orden,
        parent_id,
        destino_uso,
        tipo_subdivision,
        skipReload,
      }
    ) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.postCustom("/lotes_vacas/", {
            nombre_lote,
            codigo,
            orden: orden || 0,
            parent_id: parent_id || null,
            destino_uso: destino_uso || null,
            tipo_subdivision: tipo_subdivision || "libre",
          }),
        errorMsg: "Error al crear el lote",
      }).then((data) => {
        if (!skipReload) dispatch("cargarMapaCompleto");
        return data;
      });
    },

    async editarLoteInfo(
      { dispatch },
      {
        loteId,
        nombre_lote,
        codigo,
        habilitado,
        parent_id,
        destino_uso,
        tipo_subdivision,
      }
    ) {
      const payload = {};
      if (nombre_lote !== undefined) payload.nombre_lote = nombre_lote;
      if (codigo !== undefined) payload.codigo = codigo;
      if (habilitado !== undefined) payload.habilitado = habilitado;
      if (parent_id !== undefined) payload.parent_id = parent_id;
      if (destino_uso !== undefined) payload.destino_uso = destino_uso;
      if (tipo_subdivision !== undefined)
        payload.tipo_subdivision = tipo_subdivision;

      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(
            `/lotes_vacas/editar_estancias_nombres/${loteId}`,
            payload
          ),
        errorMsg: "Error al editar los datos del lote",
      }).then((data) => {
        dispatch("cargarMapaCompleto");
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    /**
     * Cambia el tipo_subdivision de un lote (Herencia de Tabla Única).
     * Controla qué algoritmo se ejecuta: NDVI (pasturas) o Índice Barro (corrales).
     * @param {number} loteId - ID del lote
     * @param {string} tipo_subdivision - "pastura"|"verdeo"|"siembra"|"corral_engorde"|"enfermeria"|"libre"
     */
    async cambiarTipoSubdivision(
      { dispatch, commit },
      { loteId, tipo_subdivision }
    ) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(
            `/lotes_vacas/editar_estancias_nombres/${loteId}`,
            {
              tipo_subdivision,
            }
          ),
        errorMsg: "Error al cambiar el tipo de subdivisión del lote",
      }).then((data) => {
        commit("updateLoteMapeado", { lote_id: loteId, tipo_subdivision });
        dispatch("cargarMapaCompleto");
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    /**
     * Vincula (o desvincula) un lote GIS a un corral feedlot existente.
     * Solo aplica para tipo_subdivision = "corral_engorde" o "enfermeria".
     * @param {number} loteId - ID del lote
     * @param {number|null} corralFeedlotId - ID del corral feedlot, o null para desvincular
     */
    async vincularCorralFeedlot(
      { dispatch, commit },
      { loteId, corralFeedlotId }
    ) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.patchCustom(
            `/lotes_vacas/editar_estancias_nombres/${loteId}`,
            {
              corral_feedlot_id: corralFeedlotId,
            }
          ),
        errorMsg: "Error al vincular el corral feedlot al lote",
      }).then((data) => {
        commit("updateLoteMapeado", {
          lote_id: loteId,
          corral_feedlot_id: corralFeedlotId,
        });
        dispatch("cargarEstadoAgronomico", loteId);
        return data;
      });
    },

    /**
     * Carga el Índice de Estrés por Barro de un corral de engorde.
     * Solo disponible para lotes con tipo_subdivision = "corral_engorde" o "enfermeria".
     * @param {number} loteId - ID del lote/corral
     */
    async cargarIndiceBarro({ dispatch, commit }, loteId) {
      return await dispatch("executeCall", {
        actionFn: () =>
          apiService.getCustom(`/lotes_vacas/${loteId}/indice-barro`),
        errorMsg: "Error al cargar el índice de barro del corral",
      }).then((data) => {
        commit("updateLoteMapeado", {
          lote_id: loteId,
          indice_barro: data.indice_barro,
          alerta_barro: data.alerta,
          lluvia_acumulada_7d: data.lluvia_acumulada_7d,
        });
        return data;
      });
    },
  },
};
